import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

/**
 * Scroll-triggered reveal for the Popular Plants section:
 * label, title, subtitle, carousel wrap, hint.
 */
export class PopularPlantsReveal {
  private section: HTMLElement | null = null;
  private label: HTMLElement | null = null;
  private title: HTMLElement | null = null;
  private subtitle: HTMLElement | null = null;
  private carouselWrap: HTMLElement | null = null;
  private hint: HTMLElement | null = null;
  private timeline: gsap.core.Timeline | null = null;

  constructor() {
    this.section = document.querySelector('.popular-plants-section');
    if (!this.section) return;

    this.label = this.section.querySelector('.popular-plants-label');
    this.title = this.section.querySelector('.popular-plants-title');
    this.subtitle = this.section.querySelector('.popular-plants-subtitle');
    this.carouselWrap = this.section.querySelector('.popular-plants-carousel-wrap');
    this.hint = this.section.querySelector('.popular-plants-hint');

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion) {
      gsap.set(
        [this.label, this.title, this.subtitle, this.carouselWrap, this.hint].filter(Boolean),
        { opacity: 1, y: 0 }
      );
      return;
    }

    this.setInitialStates();
    this.setupScrollReveal();
  }

  private setInitialStates(): void {
    const elements = [this.label, this.title, this.subtitle, this.carouselWrap, this.hint].filter(Boolean);
    elements.forEach((el) => gsap.set(el, { opacity: 0, y: 36, force3D: true }));
  }

  private setupScrollReveal(): void {
    if (!this.section) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: this.section,
        start: 'top 88%',
        once: true,
        invalidateOnRefresh: true,
      },
      defaults: { ease: 'power3.out', force3D: true },
    });

    if (this.label) tl.to(this.label, { opacity: 1, y: 0, duration: 0.5 }, 0);
    if (this.title) {
      tl.to(this.title, {
        opacity: 1,
        y: 0,
        duration: 0.8,
        ease: 'back.out(1.2)',
      }, 0.08);
    }
    if (this.subtitle) tl.to(this.subtitle, { opacity: 1, y: 0, duration: 0.55 }, 0.2);
    if (this.carouselWrap) tl.to(this.carouselWrap, { opacity: 1, y: 0, duration: 0.7 }, 0.35);
    if (this.hint) tl.to(this.hint, { opacity: 1, y: 0, duration: 0.45 }, 0.5);

    tl.set([this.label, this.title, this.subtitle, this.carouselWrap, this.hint].filter(Boolean), {
      clearProps: 'transform',
    });

    this.timeline = tl;
  }

  dispose(): void {
    this.timeline?.scrollTrigger?.kill();
    this.timeline?.kill();
    this.timeline = null;
    this.section = null;
    this.label = null;
    this.title = null;
    this.subtitle = null;
    this.carouselWrap = null;
    this.hint = null;
  }
}
